import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Plus,
  Edit,
  Trash2,
  BookOpen,
  Calendar,
  Users,
  Loader2,
  Search,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface ReadingPlansProps {
  user: AdminUser | null
}

interface ReadingPlan {
  id: string
  title: string
  description: string | null
  duration: string
  total_days: number
  category: 'beginner' | 'intermediate' | 'advanced' | 'topical'
  books: string[] | null
  is_public: boolean
  is_active: boolean
  created_at: string
  user_count?: number
}

const CATEGORIES = [
  { value: 'beginner', label: 'Beginner', color: 'bg-green-500' },
  { value: 'intermediate', label: 'Intermediate', color: 'bg-blue-500' },
  { value: 'advanced', label: 'Advanced', color: 'bg-purple-500' },
  { value: 'topical', label: 'Topical', color: 'bg-amber-500' },
]

const BIBLE_BOOKS = [
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
  'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel',
  '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra',
  'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs',
  'Ecclesiastes', 'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations',
  'Ezekiel', 'Daniel', 'Hosea', 'Joel', 'Amos',
  'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
  'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
  'Matthew', 'Mark', 'Luke', 'John', 'Acts',
  'Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians',
  '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews',
  'James', '1 Peter', '2 Peter', '1 John', '2 John', '3 John', 'Jude', 'Revelation',
]

export default function ReadingPlans({ user }: ReadingPlansProps) {
  const [plans, setPlans] = useState<ReadingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<ReadingPlan | null>(null)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showBookSelector, setShowBookSelector] = useState(false)

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [totalDays, setTotalDays] = useState('30')
  const [category, setCategory] = useState<string>('beginner')
  const [selectedBooks, setSelectedBooks] = useState<string[]>([])
  const [isPublic, setIsPublic] = useState(false)
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    loadPlans()
  }, [])

  async function loadPlans() {
    try {
      setLoading(true)
      setError(null)

      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) {
        console.log('No auth user found')
        setError('Not authenticated. Please log in.')
        setPlans([])
        setLoading(false)
        return
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', authUser.id)
        .single()

      if (profileError) {
        console.log('Error fetching profile:', profileError)
        setError('Error loading profile.')
        setPlans([])
        setLoading(false)
        return
      }

      if (!profile?.church_id) {
        console.log('No church_id found for user')
        setError('No church associated with your account.')
        setPlans([])
        setLoading(false)
        return
      }

      const { data, error: plansError } = await supabase
        .from('reading_plans')
        .select('*')
        .eq('church_id', profile.church_id)
        .order('created_at', { ascending: false })

      if (plansError) {
        console.error('Error loading reading plans:', plansError)
        // Table might not exist yet - this is OK
        if (plansError.code === '42P01') {
          setError('Reading plans table does not exist yet. Please run the database migration.')
        }
        setPlans([])
        return
      }

      setPlans(data || [])
    } catch (err) {
      console.error('Error loading reading plans:', err)
      setError('Failed to load reading plans.')
      setPlans([])
    } finally {
      setLoading(false)
    }
  }

  function resetForm() {
    setTitle('')
    setDescription('')
    setTotalDays('30')
    setCategory('beginner')
    setSelectedBooks([])
    setIsPublic(false)
    setIsActive(true)
    setEditingPlan(null)
  }

  function openCreateDialog() {
    resetForm()
    setIsDialogOpen(true)
  }

  function openEditDialog(plan: ReadingPlan) {
    setEditingPlan(plan)
    setTitle(plan.title)
    setDescription(plan.description || '')
    setTotalDays(plan.total_days.toString())
    setCategory(plan.category)
    setSelectedBooks(plan.books || [])
    setIsPublic(plan.is_public)
    setIsActive(plan.is_active)
    setIsDialogOpen(true)
  }

  function getDuration(days: number): string {
    if (days === 1) return '1 day'
    if (days < 7) return `${days} days`
    if (days === 7) return '1 week'
    if (days < 30) return `${Math.ceil(days / 7)} weeks`
    if (days === 30) return '1 month'
    if (days < 365) return `${Math.ceil(days / 30)} months`
    if (days === 365) return '1 year'
    return `${days} days`
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()

    if (!title.trim()) {
      alert('Please enter a plan title')
      return
    }

    const days = parseInt(totalDays, 10)
    if (isNaN(days) || days < 1) {
      alert('Please enter a valid number of days')
      return
    }

    setSaving(true)

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', authUser.id)
        .single()

      if (!profile?.church_id) {
        alert('No church ID found')
        return
      }

      const planData = {
        church_id: profile.church_id,
        title: title.trim(),
        description: description.trim() || null,
        duration: getDuration(days),
        total_days: days,
        category,
        books: selectedBooks.length > 0 ? selectedBooks : null,
        is_public: isPublic,
        is_active: isActive,
        // Note: created_by should be a member_id, not auth user id
        // We'll leave it null for now - can be set if we have member lookup
      }

      if (editingPlan?.id) {
        const { error } = await supabase
          .from('reading_plans')
          .update(planData)
          .eq('id', editingPlan.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('reading_plans')
          .insert(planData)

        if (error) throw error
      }

      setIsDialogOpen(false)
      resetForm()
      await loadPlans()
    } catch (error) {
      console.error('Error saving plan:', error)
      alert('Failed to save reading plan')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(plan: ReadingPlan) {
    if (!confirm(`Are you sure you want to delete "${plan.title}"? This cannot be undone.`)) {
      return
    }

    try {
      const { error } = await supabase
        .from('reading_plans')
        .delete()
        .eq('id', plan.id)

      if (error) throw error
      await loadPlans()
    } catch (error) {
      console.error('Error deleting plan:', error)
      alert('Failed to delete plan')
    }
  }

  async function toggleActive(plan: ReadingPlan) {
    try {
      const { error } = await supabase
        .from('reading_plans')
        .update({ is_active: !plan.is_active })
        .eq('id', plan.id)

      if (error) throw error
      await loadPlans()
    } catch (error) {
      console.error('Error updating plan:', error)
    }
  }

  function toggleBook(book: string) {
    if (selectedBooks.includes(book)) {
      setSelectedBooks(selectedBooks.filter(b => b !== book))
    } else {
      setSelectedBooks([...selectedBooks, book])
    }
  }

  const filteredPlans = plans.filter(plan =>
    plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getCategoryBadge = (cat: string) => {
    const category = CATEGORIES.find(c => c.value === cat)
    return category ? (
      <Badge className={`${category.color} text-white`}>
        {category.label}
      </Badge>
    ) : null
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reading Plans</h1>
          <p className="text-muted-foreground">
            Create and manage Bible reading plans for your congregation
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Create Plan
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Reading Plans</CardTitle>
              <CardDescription>
                {plans.length} plan{plans.length !== 1 ? 's' : ''} created
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search plans..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-[300px]"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No reading plans yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first reading plan to help members grow in their faith.
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Create Plan
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Books</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPlans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{plan.title}</div>
                        {plan.description && (
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {plan.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getCategoryBadge(plan.category)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {plan.duration}
                      </div>
                    </TableCell>
                    <TableCell>
                      {plan.books && plan.books.length > 0 ? (
                        <span className="text-sm">
                          {plan.books.length} book{plan.books.length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={plan.is_active}
                          onCheckedChange={() => toggleActive(plan)}
                        />
                        <span className={plan.is_active ? 'text-green-600' : 'text-muted-foreground'}>
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(plan)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(plan)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Edit Reading Plan' : 'Create Reading Plan'}
            </DialogTitle>
            <DialogDescription>
              {editingPlan
                ? 'Update the reading plan details below.'
                : 'Create a new Bible reading plan for your congregation.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Plan Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., The Gospels in 30 Days"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this plan covers and who it's for..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="totalDays">Number of Days *</Label>
                <Input
                  id="totalDays"
                  type="number"
                  min="1"
                  value={totalDays}
                  onChange={(e) => setTotalDays(e.target.value)}
                  placeholder="30"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Books Covered</Label>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between"
                onClick={() => setShowBookSelector(!showBookSelector)}
              >
                {selectedBooks.length > 0
                  ? `${selectedBooks.length} book${selectedBooks.length !== 1 ? 's' : ''} selected`
                  : 'Select Bible books...'}
              </Button>

              {showBookSelector && (
                <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
                  <div className="grid grid-cols-3 gap-2">
                    {BIBLE_BOOKS.map((book) => (
                      <label
                        key={book}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBooks.includes(book)}
                          onChange={() => toggleBook(book)}
                          className="rounded"
                        />
                        {book}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {selectedBooks.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedBooks.slice(0, 5).map((book) => (
                    <Badge key={book} variant="secondary">
                      {book}
                    </Badge>
                  ))}
                  {selectedBooks.length > 5 && (
                    <Badge variant="outline">+{selectedBooks.length - 5} more</Badge>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border rounded-lg p-4">
              <div>
                <Label>Active</Label>
                <p className="text-sm text-muted-foreground">
                  Members can see and join this plan
                </p>
              </div>
              <Switch
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>

            <div className="flex items-center justify-between border rounded-lg p-4">
              <div>
                <Label>Public Plan</Label>
                <p className="text-sm text-muted-foreground">
                  Available to all churches using FaithYoke
                </p>
              </div>
              <Switch
                checked={isPublic}
                onCheckedChange={setIsPublic}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
